import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Upload } from 'lucide-react';

interface Step1OrganizationProps {
  organizationData: {
    name: string;
    logoFile: File | null;
    logoPreview: string;
    adminEmail: string;
    phone: string;
    address: string;
    description: string;
  };
  onChange: (field: string, value: string) => void;
}

export const Step1Organization: React.FC<Step1OrganizationProps> = ({ organizationData, onChange }) => {
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo file must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      onChange('logoFile', file);
      const reader = new FileReader();
      reader.onloadend = () => {
        onChange('logoPreview', reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Building2 className="h-8 w-8 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">Organization Configuration</h2>
          <p className="text-muted-foreground">Set up your organization's basic information</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="orgName">Organization Name *</Label>
          <Input
            id="orgName"
            value={organizationData.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="e.g., Growth Energy Tanzania"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="adminEmail">Admin Email *</Label>
          <Input
            id="adminEmail"
            type="email"
            value={organizationData.adminEmail}
            onChange={(e) => onChange('adminEmail', e.target.value)}
            placeholder="admin@organization.com"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Contact Phone</Label>
          <Input
            id="phone"
            type="tel"
            value={organizationData.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder="+255 XXX XXX XXX"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            value={organizationData.address}
            onChange={(e) => onChange('address', e.target.value)}
            placeholder="Organization address"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Organization Description</Label>
        <Textarea
          id="description"
          value={organizationData.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Brief description of your organization..."
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="logo">Organization Logo</Label>
        <div className="flex items-start gap-4">
          {organizationData.logoPreview ? (
            <div className="relative">
              <img
                src={organizationData.logoPreview}
                alt="Logo preview"
                className="h-24 w-24 object-contain border rounded-lg bg-muted"
              />
              <button
                type="button"
                onClick={() => {
                  onChange('logoFile', null);
                  onChange('logoPreview', '');
                }}
                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full h-6 w-6 flex items-center justify-center text-xs"
              >
                ×
              </button>
            </div>
          ) : (
            <label
              htmlFor="logo"
              className="h-24 w-24 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <Upload className="h-6 w-6 text-muted-foreground" />
            </label>
          )}
          <input
            id="logo"
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
          />
          <div className="text-sm text-muted-foreground">
            <p>Upload your organization logo</p>
            <p className="text-xs mt-1">PNG, JPG up to 5MB</p>
          </div>
        </div>
      </div>
    </div>
  );
};
